import { create } from 'zustand';

/*
    Something happened, and it can be taken back.

    Mail is the one application where a misplaced click is expensive: archive is one key away from
    delete, and both act on the message somebody is looking at. Every mail client worth using
    answers this the same way — do the thing immediately, say so quietly, and leave the way back
    open for a few seconds. Confirmation dialogs answer it badly: they interrupt the ninety-nine
    correct deletions to catch the one mistake, and people learn to dismiss them without reading.

    So this is not a notification system. It is the undo shelf, and the only reason it draws
    anything on screen is to hold the button.

    The message is a key rather than a sentence: the interface is in two languages and a store is
    the wrong place to decide which one somebody reads.
*/

export interface Toast {
    id: number;
    /** i18n key for the line. */
    message: string;
    /** Values the line interpolates, already in the reader's language where they are names. */
    values?: Record<string, string>;
    /** What taking it back does. Absent when there is no way back. */
    undo?: () => void;
    /** How long it stays, in ms. */
    life: number;
}

interface ToastState {
    toasts: Toast[];
    show: (t: Omit<Toast, 'id' | 'life'> & { life?: number }) => void;
    dismiss: (id: number) => void;
}

/*
    Five seconds.

    Long enough to notice a mistake and reach the button, short enough not to sit over the message
    list. Undo is offered, not guarded: the action already happened on the server, and the shelf
    only holds the way back.
*/
const LIFE = 5000;

let nextId = 1;

export const useToasts = create<ToastState>((set) => ({
    toasts: [],

    show: ({ message, values, undo, life = LIFE }) => {
        const id = nextId++;
        /*
            One at a time.

            Deleting six messages in a row should leave one offer to undo the last one, not six
            offers stacked up the side of the screen — and the earlier ones are stale anyway: their
            undo would put a message back while the reader is busy with the next.
        */
        set({ toasts: [{ id, message, values, undo, life }] });
        setTimeout(() => {
            set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
        }, life);
    },

    dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));
